import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Row, Col, Button, CardHeader, CardTitle } from 'reactstrap';
import * as countriesLocation from '../Services/countries';
import * as userService from '../Services/userService';
import { useForm } from 'react-hook-form';
import FormInput from '../Components/ReactHookFormCustom/FormInput';
import FormProfileImageHandler from '../Components/ReactHookFormCustom/FormProfileImageHandler';
import FormRichTextBox from '../Components/ReactHookFormCustom/FormRichTextBox';
import FormLocationFull from '../Components/ReactHookFormCustom/FormLocationFull';
import { SpinnerLoader } from '../Components/shared/spinnerLoader';
import useCompanyStore from '../../../stores/companyStore';

const CompanyProfile = ({ companyFromDbToEdit }) => {
  /*global WEB_PORTAL_URL */
  const jobPortalEndpoint = `${WEB_PORTAL_URL}/company-details/`;

  const { handleSubmit, setValue, reset, control, formState } = useForm({});
  const { t } = useTranslation();

  const [address, setAddress] = useState();
  const [addressFromDbToEdit, setAddressFromDbToEdit] = useState();
  const [countries, setCountries] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setCountries(countriesLocation.getCountryEN());
  }, []);

  const company = useCompanyStore((state) => state.company);
  const updateCompany = useCompanyStore((state) => state.updateCompany);

  useEffect(() => {
    if (countries && company) {
      composeAddress(company);
    }
  }, [countries, company]);

  // useEffect(() => {
  //   if (address) {
  //     const countryOption = countries.find(
  //       (c) => c.value.toLowerCase() === address?.selectedCountry?.value?.toLowerCase()
  //     );

  //     reset({
  //       ...company,
  //       countryControllerSelect: countryOption,
  //       cityControllerSelect: address?.city,
  //       streetControllerSelect: address?.street,
  //     });
  //   }
  // }, [address]);
  useEffect(() => {
    // Always reset with company data
    const baseData = { ...company };
    if (address) {
      const countryOption = countries.find(
        (c) => c.value.toLowerCase() === address?.selectedCountry?.value?.toLowerCase()
      );
      reset({
        ...baseData,
        countryControllerSelect: countryOption,
        cityControllerSelect: address?.city,
        streetControllerSelect: address?.street,
      });
    } else {
      reset(baseData);
    }
  }, [address, company, countries, reset]);

  const composeAddress = (company) => {
    if (company.addresses && company.addresses.length > 0) {
      const addressTmp = company.addresses[0];

      const selected = countries.filter((country) => {
        return country.value === addressTmp.countryCode;
      });

      const selectedCountry = selected[0];
      const cityTmp = { label: addressTmp.city, value: addressTmp.city };

      const street = addressTmp.street;
      const streetTmp = { label: street, value: street };

      const tmpLoc = {
        selectedCountry: selectedCountry,
        googlePlaceId: addressTmp.googlePlaceId,
        city: cityTmp,
        street: streetTmp,
        latForStreetSearch: addressTmp.latForStreetSearch,
        lngForStreetSearch: addressTmp.lngForStreetSearch,
        lat: addressTmp.lat !== undefined ? addressTmp.lat : addressTmp.latLng.y,
        lng: addressTmp.lng !== undefined ? addressTmp.lng : addressTmp.latLng.x,
        disabledCitySelect: false,
        disabledStreetSelect: false,
      };

      if (companyFromDbToEdit) {
        setAddressFromDbToEdit(tmpLoc);
      } else {
        setAddress(tmpLoc);
      }
    }
  };
  const handleValidCompanyProfileSubmit = async (values, event) => {
    const companyData = {
      ...values,
      id: company.id,
    };

    setIsSaving(true);
    try {
      if (companyFromDbToEdit) {
        await userService.addOrUpdateCompanyAsAdmin(companyData);
      } else {
        await updateCompany(companyData);
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Admin can select other companies and edit them
  const editCompany = companyFromDbToEdit ? companyFromDbToEdit : company;

  return (
    <Card className="card-default mb-1">
      <CardHeader>
        <CardTitle>
          <h3>{t('settings.COMPANY_PROFILE')}</h3>
        </CardTitle>
      </CardHeader>
      <div className="card-body">
        <form onSubmit={handleSubmit(handleValidCompanyProfileSubmit)}>
          <Row className="align-items-left pb-3 justify-content-left">
            <Col md="6" style={{ maxWidth: '120px', minWidth: '30px' }}>
              <FormProfileImageHandler
                control={control}
                id="imgCompanyLogoId"
                name="imgCompanyLogo"
                imgUrl={editCompany?.logoPhotoUrl ? editCompany?.logoPhotoUrl : null}
                setValue={setValue}
                replacementImg={'img/user/company-logo-dummy.jpeg'}
              />
            </Col>
            <Col md={6}>
              <FormInput
                control={control}
                name="name"
                label={t('settings.COMPANY_NAME')}
                rules={{
                  required: t('common.REQUIRED'),
                }}
              />
            </Col>
          </Row>
          <Row className="pb-3">
            <Col md={6}>
              <FormInput control={control} name="website" label={t('settings.COMPANY_WEBSITE')} />
            </Col>
            <Col md={6}>
              <FormInput control={control} name="facebookUrl" label={t('settings.FACEBOOK_URL')} />
            </Col>
          </Row>
          <Row className="pb-3">
            <Col md={6}>
              <FormInput control={control} name="twitterHandle" label={t('settings.TWITTER_URL')} />
            </Col>
            <Col md={6}>
              <FormInput control={control} name="linkedInUrl" label={t('settings.LINKEDIN_URL')} />
            </Col>
          </Row>
          <Row className="pb-3">
            <Col md={6}>
              <FormInput control={control} name="instagramUrl" label={t('settings.INSTAGRAM_URL')} />
            </Col>
            <Col md={6}>
              <FormInput control={control} name="linkToVideo" label={t('settings.LINK_TO_REP_VIDEO')} />
            </Col>
          </Row>
          <Row className="pb-3">
            <FormLocationFull control={control} setValue={setValue} address={address} />
          </Row>
          <Row className="pb-3">
            <Col>
              <FormRichTextBox
                control={control}
                name="description"
                id="descriptionId"
                label={t('settings.COMPANY_DESCRIPTION')}
                rules={{
                  required: t('common.REQUIRED'),
                }}
              />
            </Col>
          </Row>
          <Row>
            <Col className="d-flex justify-content-end">
              <Button color="primary" disabled={isSaving} className="mr-2" type="submit">
                {isSaving && <div className="spinner-border spinner-border-sm mr-2" role="status" />}
                {t('common.SAVE')}
              </Button>
              {/* ADMIN PART */}
              {companyFromDbToEdit ? (
                <Button color="primary" disabled={isSaving} className="mr-2" type="submit">
                  {isSaving && <div className="spinner-border spinner-border-sm mr-2" role="status" />}
                  {t('common.SAVE_AS_ADMIN')}
                </Button>
              ) : null}
              <a
                className="btn btn-info"
                href={jobPortalEndpoint + editCompany?.id}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('common.SAVE_PREVIEW')}
              </a>
            </Col>
          </Row>
        </form>
      </div>
    </Card>
  );
};

export default CompanyProfile;
